import { FC, SyntheticEvent } from 'react';

import { useFormik } from 'formik';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { initialValues as defaultInitialValues, validationSchema } from './form';

import { Switch } from 'v2/components/switch/Switch';
import { TelegramAuthBotFormValues, TelegramAuthBotRequestBody } from 'v2/types/TelegramAuthBotModel';

interface TelegramAuthBotFormDialogProps {
  title: string;
  isOpen: boolean;
  onClose: VoidFunction;
  domainId: string;
  initialValues?: TelegramAuthBotFormValues;
  isSubmitting: boolean;
  onSubmit: (values: TelegramAuthBotRequestBody) => void;
}

export const TelegramAuthBotFormDialog: FC<TelegramAuthBotFormDialogProps> = ({
  title,
  isOpen,
  onClose,
  domainId,
  initialValues,
  isSubmitting,
  onSubmit,
}) => {
  const formik = useFormik<TelegramAuthBotFormValues>({
    initialValues: initialValues ?? defaultInitialValues,
    onSubmit: (values) => {
      const body: TelegramAuthBotRequestBody = {
        token: values.token,
        domainId,
        name: values.name || undefined,
        isActive: values.isActive,
      };
      onSubmit(body);
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true,
  });

  const handleActiveChange = (_: SyntheticEvent, checked: boolean) => {
    formik.setFieldValue('isActive', checked);
  };

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} scroll="paper" maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <form id="telegramAuthBotForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
          <TextField
            name="token"
            value={formik.values.token}
            error={!!formik.errors.token}
            helperText={formik.errors.token}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Bot Token"
            placeholder="123456:ABCdef..."
            required
          />

          <TextField
            name="name"
            value={formik.values.name}
            error={!!formik.errors.name}
            helperText={formik.errors.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Name"
          />

          <Switch name="isActive" checked={formik.values.isActive} onChange={handleActiveChange} label="Active" />
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Cancel
        </Button>

        <Button form="telegramAuthBotForm" type="submit" variant="contained" loading={isSubmitting}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
